import React from 'react';
import { Card, Typography, IconButton, Box, Avatar } from '@mui/material';
import Grid from '@mui/material/Grid';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Link as RouterLink } from "react-router-dom";
import { AppRoutes, getImageUrl } from '../../constants/routes';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';
import TrackActionsDropdown from '../TrackActionsDropdown';
import LikeButton from '../LikeButton';

// Space between stacked track cards, also used as their side inset so
// cards never touch the screen edge and the spacing reads as even.
const CARD_GAP = '.3rem';

// Playlists pass showArtwork (their tracks come from different releases),
// playContext (so plays credit the playlist's curator) and, while editing,
// onRemove. `actions` adds buttons beside the track's menu.
const SongCardListMenu = ({
  track,
  index = 1,
  albumTracks = null,
  showArtwork = false,
  playContext = null,
  onRemove = null,
  actions = null,
}) =>  {
  const { playTrack, currentTrack, isPlaying, playPause, setPlaylistAndPlay, addToQueue, playNext } = useMusicPlayer();
  
  // Early return if track is undefined or null
  if (!track) {
    return (
      <Grid item xs={12} sx={{marginBottom: CARD_GAP, paddingX: CARD_GAP, width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box'}}>
        <Card sx={{
          display: 'flex',
          padding: 1,
          justifyContent: 'center',
          alignItems: 'center',
          height: '4rem',
          borderRadius: '.4rem',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}>
          <Typography sx={{fontSize: 12, color: 'text.secondary'}}>
            Loading track...
          </Typography>
        </Card>
      </Grid>
    );
  }
  
  // Format duration function
  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Default values if no track is provided
  const title = track?.name || 'Track Title';
  const artist = track?.act_name || track?.acts || 'Artist Name';
  const duration = track?.length ? formatDuration(track.length) : '--:--';
  const trackId = track?.id;

  const handlePlayPause = () => {
    if (!track) return;
    
    // Ensure track has the correct structure for the music player
    const playableTrack = {
      ...track,
      audio_file: track?.audio_file || track?.audioFile,
      image: track?.image || track?.artwork
    };
    
    if (currentTrack?.id === track?.id && isPlaying) {
      playPause();
    } else {
      // If we have albumTracks, set up the full album playlist and play from this track
      if (albumTracks && albumTracks.length > 0) {
        const playableAlbumTracks = albumTracks.map(track => ({
          ...track,
          audio_file: track?.audio_file || track?.audioFile,
          image: track?.image || track?.artwork
        }));
        setPlaylistAndPlay(playableAlbumTracks, index - 1, playContext); // index - 1 because index is 1-based
      } else {
        // Otherwise just play the individual track
        playTrack(playableTrack);
      }
    }
  };

  const handlePlayNext = (track) => {
    playNext(track);
  };

  const handleAddToQueue = (track) => {
    addToQueue(track);
  };

  const handleAddToPlaylist = (track) => {
    // TODO: Implement add to playlist functionality
  };

  return (
    <Grid item xs={12} sx={{marginBottom: CARD_GAP, paddingX: CARD_GAP, width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box'}}>
      <Card sx={{
        display: 'flex',
        padding: 1,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 1,
        height: '4rem',
        borderRadius: '.4rem',
        cursor: 'pointer',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          transition: 'background-color 0.2s'
        }
      }}>
        <Typography sx={{fontSize: 12, minWidth: '1.5rem', paddingLeft: 1}} >{index}</Typography>

        {showArtwork && (
          <Avatar
            variant="rounded"
            src={getImageUrl(track.image, 'small', track) || undefined}
            alt=""
            sx={{ width: '2.5rem', height: '2.5rem', flexShrink: 0, bgcolor: 'action.hover', color: 'text.secondary' }}
          >
            <MusicNoteIcon fontSize="small" />
          </Avatar>
        )}

        {/* Play button - only plays music, no navigation */}
        <Box sx={{ minWidth: '1.5rem', display: 'flex', justifyContent: 'center' }}>
          <IconButton onClick={handlePlayPause} sx={{ padding: 0.5 }}>
            {currentTrack?.id === track?.id && isPlaying ? (
              <PauseIcon sx={{width: '1rem', height: '1rem',}}/>
            ) : (
              <PlayArrowIcon sx={{width: '1rem', height: '1rem',}}/>
            )}
          </IconButton>
        </Box>

        <Box sx={{ minWidth: '1.5rem', display: 'flex', justifyContent: 'center' }}>
          <LikeButton
            contentType="track"
            objectId={trackId}
            size="small"
            sx={{ 
              width: '.8rem', 
              height: '.8rem',
              color: 'text.secondary',
              '&:hover': { 
                color: 'error.main',
                bgcolor: 'action.hover' 
              }
            }}
          />
        </Box>


        {/* Track info area - clickable for navigation */}
        <Box 
          component={RouterLink}
          to={track?.slug || trackId ? AppRoutes.SinglePage.replace(':slug', track?.slug || trackId) : AppRoutes.Browsing}
          sx={{ 
            display: 'flex', 
            flex: 1, 
            py: 1,
            textDecoration: 'none',
            color: 'inherit',
            minWidth: 0,
            overflow: 'hidden',
            maxWidth: '100%'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0, overflow: 'hidden', maxWidth: '100%' }}>
            <Typography noWrap sx={{ fontSize: 12, fontWeight: 'medium', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>
              •
            </Typography>
            <Typography 
              noWrap 
              sx={{ 
                fontSize: 12,
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%'
              }}
            >
              {artist}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ minWidth: '2.5rem', display: 'flex', justifyContent: 'center' }}>
          <Typography sx={{fontSize: 12}} >{duration}</Typography>
        </Box>
        <Box sx={{ minWidth: '1.5rem', display: 'flex', justifyContent: 'center' }}>
          <TrackActionsDropdown
            track={track}
            onPlayNext={handlePlayNext}
            onAddToQueue={handleAddToQueue}
            onAddToPlaylist={handleAddToPlaylist}
          />
        </Box>
        {actions}
        {onRemove && (
          <IconButton
            size="small"
            onClick={() => onRemove(track)}
            aria-label={`Remove ${title} from playlist`}
            title="Remove from playlist"
            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
          >
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Card>
    </Grid>
  )
}

export default SongCardListMenu
